/**
 * „WAS WIRKT AUF MICH" (Rafael, 20.10.2026 08:25Z) — alle Anweisungen, die die Berater des
 * angemeldeten Partners lesen, in Lesereihenfolge; die persoenliche CLAUDE.md ist hier
 * bearbeitbar. Daten und Rechte: server/routes/anweisungen.ts. Geoeffnet ueber den Namen oben
 * rechts (ProjectTabs) bzw. im Sales-Bereich (SalesLanding).
 */
import { useCallback, useEffect, useState } from 'react';
import { httpBoundary } from '@werkingflow/trust-boundary';
import { z } from 'zod';
import { useAuth } from '../contexts/AuthContext';

const DateiS = z.object({ pfad: z.string(), zustand: z.enum(['vorhanden', 'fehlt', 'fehler']), inhalt: z.string().optional(),
  sha256: z.string().optional(), gekuerzt: z.boolean().optional(), meldung: z.string().optional() });
const SchichtS = z.object({ art: z.enum(['plattform', 'persoenlich', 'arbeitsbereich']), titel: z.string(), erklaerung: z.string(),
  text: z.string().optional(), dateien: z.array(DateiS), bearbeitbar: z.boolean().optional(), zustand: z.string().optional() });
const AntwortS = z.object({ nutzer: z.object({ id: z.string(), name: z.string() }), eigene: z.boolean(), schichten: z.array(SchichtS) });
const NutzerS = z.object({ nutzer: z.array(z.object({ id: z.string(), name: z.string() })) });
const GespeichertS = z.object({ ok: z.literal(true), sha256: z.string() });
type Datei = z.infer<typeof DateiS>;
type Schicht = z.infer<typeof SchichtS>;
type Antwort = z.infer<typeof AntwortS>;

/** Fehlertext des Servers ({error}) statt nur des Status — er sagt, was zu tun ist. */
const serverFehler = (status: number, text: string) => {
  if (status < 400) return undefined;
  let m = `HTTP ${status}`;
  try { const b = JSON.parse(text) as { error?: unknown }; if (typeof b.error === 'string') m = b.error; } catch { /* kein JSON: Status genuegt */ }
  return { throw: new Error(m) };
};

const kasten: React.CSSProperties = { border: '1px solid var(--tn-border)', borderRadius: 6, padding: 10, marginBottom: 12, background: 'var(--tn-bg)' };
const pre: React.CSSProperties = { whiteSpace: 'pre-wrap', fontSize: 12, maxHeight: 260, overflow: 'auto', background: 'var(--tn-surface, rgba(0,0,0,0.04))', padding: 8, borderRadius: 4, margin: '6px 0 0' };

function DateiAnzeige({ d }: { d: Datei }) {
  if (d.zustand === 'fehlt') return <div style={{ fontSize: 12, color: 'var(--tn-text-muted)' }}><code>{d.pfad}</code> — keine Datei</div>;
  if (d.zustand === 'fehler') return <div role="alert" style={{ fontSize: 12, color: 'var(--tn-red)' }}><code>{d.pfad}</code> — {d.meldung}</div>;
  return <div style={{ marginTop: 6 }}>
    <code style={{ fontSize: 11 }}>{d.pfad}</code>{d.gekuerzt && <span style={{ color: 'var(--tn-orange)' }}> (gekürzt angezeigt)</span>}
    <pre style={pre}>{d.inhalt || '(leer)'}</pre>
  </div>;
}

function Persoenlich({ s, onGespeichert }: { s: Schicht; onGespeichert: () => void }) {
  const d = s.dateien[0];
  const [text, setText] = useState(d?.inhalt ?? '');
  const [meldung, setMeldung] = useState<{ ok: boolean; text: string } | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  useEffect(() => { setText(d?.inhalt ?? ''); }, [d?.inhalt]);
  if (!s.bearbeitbar || !d?.sha256) return <>{s.dateien.map(x => <DateiAnzeige key={x.pfad} d={x} />)}</>;
  const geaendert = text !== (d.inhalt ?? '');
  const speichern = async () => {
    setLaeuft(true); setMeldung(null);
    // fallback-ok: der Fehler steht sichtbar neben dem Knopf (role=alert), nichts wird still verworfen.
    try {
      await httpBoundary({ name: 'cui.anweisungen.persoenlich.speichern', url: '/api/anweisungen/persoenlich', method: 'PUT',
        body: { inhalt: text, basisSha256: d.sha256 }, schema: GespeichertS, timeoutMs: 15000, onStatus: serverFehler });
      setMeldung({ ok: true, text: 'Gespeichert. Gilt ab dem nächsten Start einer Sitzung (neu oder fortgesetzt); bereits laufende lesen sie erst dann.' });
      onGespeichert();
    } catch (e) { setMeldung({ ok: false, text: e instanceof Error ? e.message : String(e) }); }
    finally { setLaeuft(false); }
  };
  return <div>
    <textarea data-ai-id="was-wirkt-persoenlich-text" aria-label="Meine persönliche Anleitung" value={text}
      onChange={e => setText(e.target.value)} rows={14}
      style={{ width: '100%', boxSizing: 'border-box', fontFamily: 'monospace', fontSize: 12, marginTop: 6, background: 'var(--tn-bg)', color: 'var(--tn-text)', border: '1px solid var(--tn-border)', borderRadius: 4, padding: 6 }} />
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
      <button type="button" data-ai-id="was-wirkt-persoenlich-speichern" disabled={!geaendert || laeuft} onClick={speichern}>
        {laeuft ? 'Speichert …' : 'Speichern'}</button>
      {geaendert && <button type="button" onClick={() => setText(d.inhalt ?? '')}>Verwerfen</button>}
      {meldung && <span role={meldung.ok ? 'status' : 'alert'} style={{ fontSize: 12, color: meldung.ok ? 'var(--tn-green)' : 'var(--tn-red)' }}>{meldung.text}</span>}
    </div>
    <div style={{ fontSize: 11, color: 'var(--tn-text-muted)', marginTop: 4 }}><code>{d.pfad}</code> — im Chat als <code>~/.claude/CLAUDE.md</code></div>
  </div>;
}

export default function WasWirktAufMich({ onClose }: { onClose: () => void }) {
  const { user } = useAuth();
  const [nutzer, setNutzer] = useState<string>('');
  const [liste, setListe] = useState<Array<{ id: string; name: string }>>([]);
  const [daten, setDaten] = useState<Antwort | null>(null);
  const [fehler, setFehler] = useState('');
  const laden = useCallback(async () => {
    setFehler('');
    // fallback-ok: der Fehler steht sichtbar oben in der Ansicht (role=alert), keine leere Ansicht als Ersatz.
    try { setDaten(await httpBoundary({ name: 'cui.anweisungen.lesen', url: '/api/anweisungen' + (nutzer ? `?nutzer=${encodeURIComponent(nutzer)}` : ''),
      schema: AntwortS, timeoutMs: 15000, onStatus: serverFehler })); }
    catch (e) { setDaten(null); setFehler(e instanceof Error ? e.message : String(e)); }
  }, [nutzer]);
  useEffect(() => { void laden(); }, [laden]);
  useEffect(() => {
    if (user?.role !== 'admin') return;
    httpBoundary({ name: 'cui.anweisungen.nutzer', url: '/api/anweisungen/nutzer', schema: NutzerS, timeoutMs: 15000, onStatus: serverFehler })
      .then(b => setListe(b.nutzer))
      .catch(e => setFehler(`Nutzerliste: ${e instanceof Error ? e.message : e}`));
  }, [user?.role]);
  useEffect(() => { const k = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); }; window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k); }, [onClose]);

  return <div role="dialog" aria-modal="true" aria-label="Was wirkt auf mich" data-ai-id="was-wirkt-auf-mich"
    onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.45)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '5vh 12px', overflow: 'auto' }}>
    <div style={{ background: 'var(--tn-bg-dark, var(--tn-bg))', color: 'var(--tn-text)', border: '1px solid var(--tn-border)', borderRadius: 8, width: 'min(900px, 100%)', padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <h2 style={{ margin: 0, fontSize: 18, flex: 1 }}>Was wirkt auf mich{daten && !daten.eigene ? ` — ${daten.nutzer.name}` : ''}</h2>
        {user?.role === 'admin' && <select aria-label="Partner ansehen" value={nutzer} onChange={e => setNutzer(e.target.value)}>
          <option value="">Ich selbst</option>
          {liste.filter(n => n.id !== user.id).map(n => <option key={n.id} value={n.id}>{n.name}</option>)}
        </select>}
        <button type="button" data-ai-id="was-wirkt-schliessen" onClick={onClose} aria-label="Schließen">✕</button>
      </div>
      <p style={{ fontSize: 13, color: 'var(--tn-text-muted)' }}>Diese Anweisungen lesen deine Berater, bevor sie antworten — von oben nach unten. Weiter unten Stehendes ist spezieller und gilt im Zweifel vor dem Allgemeinen.</p>
      {fehler && <div role="alert" style={{ color: 'var(--tn-red)', marginBottom: 12 }}>{fehler}</div>}
      {!daten && !fehler && <div>Lädt …</div>}
      {daten?.schichten.map((s, i) => <section key={i} style={kasten} data-ai-id={`was-wirkt-schicht-${s.art}`}>
        <div style={{ fontWeight: 700 }}>{i + 1}. {s.titel}</div>
        <div style={{ fontSize: 12, color: 'var(--tn-text-muted)', marginTop: 2 }}>{s.erklaerung}</div>
        {s.text && <details style={{ marginTop: 6 }}><summary style={{ cursor: 'pointer', fontSize: 12 }}>Systemtext anzeigen</summary><pre style={pre}>{s.text}</pre></details>}
        {s.art === 'persoenlich' ? <Persoenlich s={s} onGespeichert={laden} />
          : s.dateien.length ? s.dateien.map(d => <DateiAnzeige key={d.pfad} d={d} />)
          : s.art === 'arbeitsbereich' && <div style={{ fontSize: 12, color: 'var(--tn-text-muted)', marginTop: 4 }}>Keine Anleitung in diesem Arbeitsbereich.</div>}
      </section>)}
    </div>
  </div>;
}
